import { Link } from "@tanstack/react-router";
import type { Locale } from "@/lib/i18n";

const MIGRATION_RESOURCE_SLUGS = new Set([
  "shopify-plus-migration",
  "shopify-migration-guide-2026",
  "shopify-migration-timeline",
  "woocommerce-to-shopify-migration",
  "magento-to-shopify-migration-what-agencies-get-wrong",
  "bigcommerce-to-shopify-migration-guide",
  "llantas-cavazos-shopify-migration-case-study",
  "caso-llantas-cavazos-migracion-shopify",
  "shopify-meteoric-rise-2026",
]);

type ClusterLink = { key: string; href: string; label: string; description: string };

const LINKS: Record<Locale, ClusterLink[]> = {
  en: [
    { key: "guide", href: "/resources/shopify-migration-guide-2026", label: "Use the platform-neutral migration framework", description: "Plan a move from WooCommerce, Magento, BigCommerce, PrestaShop or a custom platform." },
    { key: "plus", href: "/resources/shopify-plus-migration", label: "Evaluate when Shopify Plus is justified", description: "Separate Plus-specific requirements from a standard Shopify migration." },
    { key: "replatform", href: "/resources/shopify-replatforming-playbook", label: "Run cutover and stabilization", description: "Use the governance, rollback and post-launch operating model." },
    { key: "hub", href: "/resources/migration", label: "Browse source-platform migration guides", description: "Compare the risks that change with each source platform." },
    { key: "service", href: "/shopify-migration", label: "Work with Shugert on a Shopify migration", description: "Review the implementation scope for data, SEO continuity, integrations, QA, cutover and launch." },
  ],
  es: [
    { key: "guide", href: "/es/recursos/shopify-migration-guide-2026", label: "Usa el framework neutral de migración", description: "Planea el cambio desde WooCommerce, Magento, BigCommerce, PrestaShop o una plataforma custom." },
    { key: "plus", href: "/es/recursos/shopify-plus-migration", label: "Evalúa cuándo se justifica Shopify Plus", description: "Separa requisitos específicos de Plus de una migración a Shopify estándar." },
    { key: "replatform", href: "/es/recursos/playbook-replatforming-shopify", label: "Controla cutover y estabilización", description: "Usa el modelo de gobierno, rollback y operación post-lanzamiento." },
    { key: "hub", href: "/es/recursos/migracion", label: "Explora guías por plataforma de origen", description: "Compara los riesgos que cambian según la plataforma de origen." },
    { key: "service", href: "/es/migracion-shopify", label: "Trabaja con Shugert en una migración Shopify", description: "Revisa el alcance de implementación para datos, continuidad SEO, integraciones, QA, cutover y lanzamiento." },
  ],
};

const CURRENT_PATHS: Record<Locale, Record<string, string>> = {
  en: {
    "shopify-migration-guide-2026": "/resources/shopify-migration-guide-2026",
    "shopify-plus-migration": "/resources/shopify-plus-migration",
  },
  es: {
    "shopify-migration-guide-2026": "/es/recursos/shopify-migration-guide-2026",
    "shopify-plus-migration": "/es/recursos/shopify-plus-migration",
  },
};

export function MigrationClusterLinks({ currentSlug, locale }: { currentSlug: string; locale: Locale }) {
  if (!MIGRATION_RESOURCE_SLUGS.has(currentSlug)) return null;
  const currentPath = CURRENT_PATHS[locale][currentSlug];
  const links = LINKS[locale].filter((item) => item.href !== currentPath);
  const serviceLink = links.find((item) => item.key === "service");
  const editorialLinks = links.filter((item) => item.key !== "service");
  const headingId = `migration-cluster-next-${locale}`;

  return (
    <nav aria-labelledby={headingId} data-seo-cluster="shopify-migration" className="mx-auto mt-16 max-w-5xl px-6">
      <div className="rounded-3xl border border-border bg-card/50 p-6 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {locale === "es" ? "Ruta editorial" : "Editorial path"}
        </p>
        <h2 id={headingId} className="mt-3 font-display text-2xl font-semibold tracking-tight text-foreground">
          {locale === "es" ? "Continúa con la decisión correcta" : "Continue with the right migration decision"}
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
          {locale === "es"
            ? "Cada recurso cubre una intención distinta. Elige la guía general, la evaluación de Plus, el playbook de ejecución o el alcance comercial según tu siguiente decisión."
            : "Each resource owns a distinct intent. Choose general planning, Plus qualification, execution governance or commercial scope according to the next decision."}
        </p>

        {serviceLink ? (
          <Link
            to={serviceLink.href}
            data-seo-commercial-owner="migration"
            className="mt-6 block rounded-2xl border border-foreground/20 bg-background p-5 transition-colors hover:border-foreground/40"
          >
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {locale === "es" ? "Ruta de implementación" : "Implementation path"}
            </span>
            <span className="mt-2 block text-sm font-semibold text-foreground">{serviceLink.label}</span>
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">{serviceLink.description}</span>
          </Link>
        ) : null}

        <ul className="mt-4 grid gap-3 md:grid-cols-2">
          {editorialLinks.map((item) => (
            <li key={item.key}>
              <Link to={item.href} className="block h-full rounded-2xl border border-border bg-background/70 p-4 transition-colors hover:border-foreground/30">
                <span className="text-sm font-semibold text-foreground">{item.label}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">{item.description}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
